import { Injectable } from '@angular/core';
import { BaseHttpService } from '../base-http-service.service';
import { AuthInterceptorService } from 'src/app/interceptors/auth.interceptor';

/**
 * One extension option as the quote endpoint returns it.
 *
 * The money fields are three different questions and must not be used interchangeably:
 *  - `chargeableAmountCents`  — what the guest WILL be charged (ENG-758). Display this.
 *  - `extensionChargeCents`   — the added time's own price (EAG-1303). Can exceed the above when a
 *                               prior payment already covers part of it.
 *  - `additionalAmountDueCents` — the STAFF figure: added time plus any pre-existing unpaid balance.
 *                               Never render this to a guest; at a POS-settled venue it is the whole
 *                               booking.
 * The last two are optional so an API build predating a split still deserializes.
 */
export interface TimeExtensionOptionQuote {
  extensionSeconds: number;
  chargeableAmountCents?: number;
  extensionChargeCents?: number;
  outstandingBalanceCents?: number;
  additionalAmountDueCents?: number;
  /**
   * The tax component OF `extensionChargeCents`, from the same pricing pass. The server has always
   * sent it; nothing read it, so every price in the modal was rendered as a bare number with no
   * indication tax was already in it. A Metro Golf guest was quoted "1 hour +$61.02" against a
   * posted $27/hour rate and the venue reported it as a pricing bug (ENG-899).
   *
   * Only meaningful against `extensionChargeCents`. When the displayed price is the smaller
   * `chargeableAmountCents` (a prior payment already covers part of the added time), this figure
   * does NOT describe what is being charged — see `resolveExtensionTaxCents` in the parent, which
   * drops it in that case rather than showing a tax line that does not reconcile.
   */
  extensionTaxCents?: number;
  isAvailable: boolean;
  unavailableReason?: string;
}

// Models
import { BayNeedsHelpRequest } from 'src/app/models/in-bay-management/requests/bay-needs-help.request';
import { AdditionalGuestSatisfiedRequirementsRequest } from 'src/app/models/in-bay-management/requests/set-additional-guest-satisfied-requirements.request';
import { BayAvailabilityResponse } from 'src/app/models/bay/responses/bay-availability.response';
import { KioskGuestSearchDto } from 'src/app/models/kiosk/kiosk-guest-search.model';
import { KioskBookingArea } from 'src/app/models/kiosk/kiosk-booking-area.model';
import {
  CheckGuestWaiverStatusResponse,
  BulkCheckGuestWaiverStatusRequest,
  BulkCheckGuestWaiverStatusResponse,
  CreateKioskWaiverSignatureRequest,
  KioskWaiverSignatureResponse,
  AssociateWaiversWithReservationRequest,
  AssociateWaiversWithReservationResponse,
  BulkOnBehalfWaiverSignatureRequest,
  BulkOnBehalfWaiverSignatureResponse
} from 'src/app/models/kiosk/kiosk-guest-details.model';

@Injectable({
  providedIn: 'root'
})
export class InBayService extends BaseHttpService {
  constructor(authInterceptor: AuthInterceptorService) {
    super(authInterceptor);
  }

  // ! POST Requests
  async addWaiverSignature(body: object, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Waiver/Signature`;
    return this.post<any>(endpoint, body, retry);
  }

  async addOnBehalfOfWaiverSignature(body: object, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Waiver/OnBehalfOf/Signature`;
    return this.post<any>(endpoint, body, retry);
  }

  async addMultiPersonWaiverSignature(body: object, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Waiver/MultiPerson/Signature`;
    return this.post<any>(endpoint, body, retry);
  }

  async createKioskWaiverSignature(
    request: CreateKioskWaiverSignatureRequest,
    retry = true
  ): Promise<KioskWaiverSignatureResponse> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/WaiverSignature`;
    return this.post<KioskWaiverSignatureResponse>(endpoint, request, retry);
  }

  async associateWaiversWithReservation(
    request: AssociateWaiversWithReservationRequest,
    retry = true
  ): Promise<AssociateWaiversWithReservationResponse> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/Waiver/AssociateWithReservation`;
    return this.post<AssociateWaiversWithReservationResponse>(endpoint, request, retry);
  }

  async createBulkOnBehalfWaiverSignatures(
    request: BulkOnBehalfWaiverSignatureRequest,
    retry = true
  ): Promise<BulkOnBehalfWaiverSignatureResponse> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/BulkOnBehalfWaiverSignatures`;
    return this.post<BulkOnBehalfWaiverSignatureResponse>(endpoint, request, retry);
  }

  async updateMultiPersonInfo(body: object, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/MultiPersonInfo`;
    return this.post<any>(endpoint, body, retry);
  }

  async getMultiPersonInfo(defaultBayId: string, corporationDetailId: string, reservationId: string, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/MultiPersonInfo?dbid=${encodeURIComponent(defaultBayId)}&cid=${encodeURIComponent(corporationDetailId)}&rid=${encodeURIComponent(reservationId)}`;
    return this.get<any>(endpoint, retry);
  }

  // ! GET Requests
  async getInBayDetails(defaultBayId: string, corporationDetailId: string, pageNumber: number = null, pageSize: number = null, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/ReservationByDefaultBayId?dbid=${defaultBayId}&cid=${corporationDetailId}`;
    if (pageNumber !== null && pageSize !== null) {
      endpoint + `&pageNumber=${pageNumber}&pageSize=${pageSize}`;
    }

    const response = await this.get<any>(endpoint, retry);

    // ENG-152 — record when this payload actually reached the device. serverTimeUtc is only
    // meaningful paired with the device clock as it read at that moment. The provider hands the
    // same object back to every later reader, so measuring skew when it is READ would fold the
    // payload's age into the skew and make the grace countdown run generous.
    if (response) {
      response.receivedAtDeviceMs = Date.now();
    }

    return response;
  }

  async getAllReservationGuestsAreMembers(reservationId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/AllReservationGuestsAreMembers?rid=${reservationId}`;
    return this.get<any>(endpoint, retry);
  }

  async canActivateBay(reservationId: string, corporationDetailId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/CanActivateBay?rid=${reservationId}&cid=${corporationDetailId}`;
    return this.get<any>(endpoint, retry);
  }

  async getAvailableTimeIncrements(corporationDetailId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/AvailableTimeIncrements?cid=${corporationDetailId}`;
    return this.get<any>(endpoint, retry);
  }

  async getTimeExtensionOptions(corporationDetailId: string, reservationId: string | null = null, retry = true): Promise<number[] | null> {
    // The reservation id lets the server derive the booking area so per-area
    // Min/MaxBayTimeSeconds overrides shape the extension options.
    let endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionOptions?cid=${encodeURIComponent(corporationDetailId)}`;
    if (reservationId) {
      endpoint += `&reservationId=${encodeURIComponent(reservationId)}`;
    }
    return this.get<number[]>(endpoint, retry);
  }

  // EAG-1303 — the quote response splits the charged total into `extensionChargeCents`
  // (the price of ONLY the added time, fee'd exactly like a fresh add-reservation/assign-bays
  // booking of that duration) and `outstandingBalanceCents` (any pre-existing unpaid balance
  // folded into the total). `additionalAmountDueCents` remains the conflated total. The guest
  // "Request More Time" UI must price the added time off `extensionChargeCents`, not the total.
  //
  // ENG-758 — `chargeableAmountCents` supersedes both for display: it is what the server will
  // ACTUALLY charge, `min(extensionChargeCents, totalDue)`, resolved through the same helper the
  // charge paths use. It differs from `extensionChargeCents` when a prior payment already covers
  // part of the added time (an overpayment, or a comp applied after payment), where the added
  // time's own price exceeds what is still owed and the guest would be quoted more than they are
  // billed. Prefer it; the older fields stay for API builds that predate it.
  async quoteTimeExtensionOptions(
    corporationDetailId: string,
    body: { reservationId: string; extensionSecondsOptions: number[] },
    retry = true
  ): Promise<Array<TimeExtensionOptionQuote> | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionOptions/Quote?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.post<Array<TimeExtensionOptionQuote>>(endpoint, body, retry);
  }

  // ENG-152 — which card, if any, tapping Submit will charge. `chargesAutomatically: false` is the
  // ordinary answer for an opted-out venue or a guest with no card on file, and the UI treats it as
  // "staff will confirm" rather than an error.
  async getTimeExtensionPaymentMethod(
    corporationDetailId: string,
    reservationId: string,
    retry = true
  ): Promise<{ chargesAutomatically: boolean; cardBrand: string | null; cardLast4: string | null; consentSourceLabel: string | null; automaticChargeUnavailableReason: string | null } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/PaymentMethod?cid=${encodeURIComponent(corporationDetailId)}&rid=${encodeURIComponent(reservationId)}`;
    return this.get<{ chargesAutomatically: boolean; cardBrand: string | null; cardLast4: string | null; consentSourceLabel: string | null; automaticChargeUnavailableReason: string | null }>(endpoint, retry);
  }

  async createTimeExtensionRequest(
    corporationDetailId: string,
    body: { reservationId: string; defaultBayId: string; requestedExtensionSeconds: number },
    retry = true
  ): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.post<any>(endpoint, body, retry);
  }

  // ENG-751 — the guest's session has ended (or there was never a card on file), so the server will not
  // charge anyone off-session. These two take a card the guest presents NOW: mint the PaymentIntent
  // server-side, let Stripe Elements confirm it, then have the server verify that intent before it
  // grants any time. `retry = false` on both: they are money calls, and a blind retry of a mutation is
  // not something the shared retry policy should be deciding for us.
  async createTimeExtensionPaymentIntent(
    corporationDetailId: string,
    body: { reservationId: string; defaultBayId: string; requestedExtensionSeconds: number },
    retry = false
  ): Promise<{ paymentIntentId: string; clientSecret: string; stripeAccountId: string; publishableKey: string; amountCents: number; targetSecondsBooked: number } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/PaymentIntent?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.post<{ paymentIntentId: string; clientSecret: string; stripeAccountId: string; publishableKey: string; amountCents: number; targetSecondsBooked: number }>(endpoint, body, retry);
  }

  async confirmTimeExtensionPayment(
    corporationDetailId: string,
    body: { reservationId: string; defaultBayId: string; requestedExtensionSeconds: number; paymentIntentId: string },
    retry = false
  ): Promise<{ applied: boolean; alreadyApplied: boolean; targetSecondsBooked: number } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/ConfirmPayment?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.post<{ applied: boolean; alreadyApplied: boolean; targetSecondsBooked: number }>(endpoint, body, retry);
  }

  async getActiveTimeExtensionRequest(reservationId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/Active?rid=${encodeURIComponent(reservationId)}`;
    return this.get<any>(endpoint, retry);
  }

  // ENG-754 — the identity gate. The QR on the bay is static, printed, and has been on the wall for
  // months, and the guest token behind it is minted anonymously, so nothing in the request chain says
  // WHO is holding the phone. While a staff member approved every extension, a person saw it happen;
  // auto-approve removes that person, so the guest confirms a code sent to the contact details already
  // on the reservation before a card on file can be charged. Once per session.
  //
  // `retry = false` on both mutations, same reasoning as createTimeExtensionPaymentIntent: the server
  // rate-limits them, and a blind retry spends the guest's 60-second cooldown or one of their eight
  // attempts on a request they never made twice.

  /**
   * Sends a 6-digit code to the reservation's phone or email. A 429 means a send is already in
   * cooldown, which is not a failure the guest caused — it carries the seconds remaining so the UI can
   * count down rather than showing an error.
   */
  async sendTimeExtensionVerification(
    corporationDetailId: string,
    body: {
      reservationId: string;
      defaultBayId: string;
      requestedExtensionSeconds: number;
      channel: 'sms' | 'email';
    },
    retry = false
  ): Promise<{
    sent: boolean;
    maskedDestination: string | null;
    cooldownSecondsRemaining: number | null;
    failureReason: string | null;
  } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/SendVerification?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.postExpecting4xx(endpoint, body, [429, 400], retry);
  }

  /**
   * Confirms either the 6-digit code the guest typed or the token from an emailed link. A wrong code
   * comes back 400 WITH `attemptsRemaining` — the guest has to be told how many tries are left before
   * the lockout, so that body is an answer, not an error.
   */
  async verifyTimeExtension(
    corporationDetailId: string,
    body: { reservationId?: string; code?: string; token?: string },
    retry = false
  ): Promise<{ verified: boolean; attemptsRemaining: number | null; failureReason: string | null } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/Verify?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.postExpecting4xx(endpoint, body, [400], retry);
  }

  /**
   * Whether this reservation is already verified for the session, plus the masked destinations. Read on
   * modal open so a second extension skips the gate entirely and the channel list is ready before it is
   * needed.
   */
  async getTimeExtensionVerificationState(
    corporationDetailId: string,
    reservationId: string,
    retry = true
  ): Promise<{
    isVerified: boolean;
    maskedPhone: string | null;
    maskedEmail: string | null;
    cooldownSecondsRemaining: number | null;
  } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/TimeExtensionRequest/VerificationState?cid=${encodeURIComponent(corporationDetailId)}&rid=${encodeURIComponent(reservationId)}`;
    return this.get<any>(endpoint, retry);
  }

  /**
   * The shared HTTP layer throws on 4xx and, since ENG-754, hangs the parsed body off the error. On the
   * verification endpoints some 4xx codes ARE the answer — a wrong code carries the attempts remaining,
   * a send inside the cooldown carries the seconds left — so those come back as a value. Anything else
   * is a real failure and is rethrown so the caller's catch still runs.
   */
  private async postExpecting4xx<T>(
    endpoint: string,
    body: object,
    expectedStatuses: number[],
    retry: boolean
  ): Promise<T | null> {
    try {
      return await this.post<T>(endpoint, body, retry);
    } catch (error: any) {
      if (expectedStatuses.includes(error?.status)) {
        return error.data ?? null;
      }
      throw error;
    }
  }

  async getRequirementDetailsByPhoneNumber(phone: string, reservationId: string, corporationDetailId: string, defaultBayId: string, retry = true) {
    const trimmedPhone = (phone ?? '').toString().trim();
    const endpoint = `/spotlite-proxy/Spotlite/InBay/RequirementDetailsByPhoneNumber?phone=${encodeURIComponent(trimmedPhone)}&rid=${encodeURIComponent(reservationId)}&cid=${encodeURIComponent(corporationDetailId)}&dbid=${encodeURIComponent(defaultBayId)}`;
    return this.get<any>(endpoint, retry);
  }

  async getVenueWaiver(corporationDetailId: string, bookingAreaId?: string, retry = true) {
    let endpoint = `/spotlite-proxy/Waiver?cid=${corporationDetailId}`;
    if (bookingAreaId) {
      endpoint += `&bookingAreaId=${encodeURIComponent(bookingAreaId)}`;
    }
    return this.get<any>(endpoint, retry);
  }

  async getVenueMinimumMembership(corporationDetailId: string, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/MinimumMembership?cid=${corporationDetailId}`;
    return this.get<any>(endpoint, retry);
  }
  
  async checkExistingMembershipsForGuests(body: any, retry = true) {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Membership/CheckExistingMemberships`;
    return this.post<any>(endpoint, body, retry);
  }
  
  async getPreviousWaiverSignatures(retry = true) {
    // Fetch previous waiver signers for the current user
    const endpoint = `/spotlite-proxy/Spotlite/InBay/PreviousWaiverSignatures`;
    return this.get<any[]>(endpoint, retry);
  }

  async getAggregatedOnBehalfOfGuests(guestProfileVenueId: string, retry = true) {
    // Fetch previous waiver signers for the current user
    const endpoint = `/spotlite-proxy/Spotlite/InBay/AggregatedOnBehalfOfGuests?gpvid=${guestProfileVenueId}`;
    return this.get<any[]>(endpoint, retry);
  }

  /**
   * Check if a guest has signed the current venue waiver
   */
  async checkGuestWaiverStatus(
    corporationDetailId: string, 
    phoneNumber?: string, 
    guestProfileVenueId?: string,
    bookingAreaId?: string,
    retry = true
  ): Promise<CheckGuestWaiverStatusResponse> {
    const params = new URLSearchParams();
    params.append('corporationDetailId', corporationDetailId);
    if (phoneNumber) {
      params.append('phoneNumber', phoneNumber.toString().trim());
    }
    if (guestProfileVenueId) {
      params.append('guestProfileVenueId', guestProfileVenueId);
    }
    if (bookingAreaId) {
      params.append('bookingAreaId', bookingAreaId);
    }
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/CheckGuestWaiverStatus?${params.toString()}`;
    return this.get<CheckGuestWaiverStatusResponse>(endpoint, retry);
  }

  /**
   * Check waiver status for multiple guests
   */
  async checkMultipleGuestWaiverStatus(
    request: BulkCheckGuestWaiverStatusRequest, 
    retry = true
  ): Promise<BulkCheckGuestWaiverStatusResponse> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/CheckMultipleGuestWaiverStatus`;
    return this.post<BulkCheckGuestWaiverStatusResponse>(endpoint, request, retry);
  }

  async getTotalReservationGuestsAllowed(corporationDetailId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Widget/GetTotalGuestsAllowed?cid=${corporationDetailId}`;
    return this.get<any>(endpoint, retry);
  }

  async getAvailableReservationDurations(corporationDetailId: string, isEvent: boolean = false, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Widget/AvailableReservationDurations?cid=${encodeURIComponent(corporationDetailId)}&isEvent=${isEvent}`;
    return this.get<any>(endpoint, retry);
  }

  async getVenueCurrentISOTime(corporationDetailId: string, retry = true): Promise<{ currentTime: string; timeZone: string } | null> {
    const endpoint = `/spotlite-proxy/Widget/GetVenueCurrentISOTime?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get<{ currentTime: string; timeZone: string }>(endpoint, retry);
  }

  // Returns the visible, non-default BookingAreas for a venue. Empty array when the venue has only
  // the Default area (kiosk picker hides itself in that case — see kiosk plan locked decision #1).
  async getKioskBookingAreas(corporationDetailId: string, retry = true): Promise<KioskBookingArea[] | null> {
    const endpoint = `/spotlite-proxy/Widget/BookingAreas?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get<KioskBookingArea[]>(endpoint, retry);
  }

  // bookingAreaId: when set, area's rate rows for the DOW win over venue rates (backend per-DOW fallback).
  async getVenueHourlyRates(corporationDetailId: string, dayOfWeek: number, date?: string, bookingAreaId?: string | null, retry = true): Promise<any[] | null> {
    let endpoint = `/spotlite-proxy/Widget/VenueHourlyRates?cid=${encodeURIComponent(corporationDetailId)}&dow=${dayOfWeek}`;
    if (date) endpoint += `&date=${encodeURIComponent(date)}`;
    if (bookingAreaId) endpoint += `&bookingAreaId=${encodeURIComponent(bookingAreaId)}`;
    return this.get<any[]>(endpoint, retry);
  }

  // bookingAreaId: when set, area hours override venue hours for the day if defined; otherwise the
  // backend falls back per-field (kiosk plan locked decision: per-field fallback to venue defaults).
  // reservationPastCloseBufferSeconds: area-resolved past-close grace. A reservation may legitimately
  // END up to this many seconds past venueClosingTime, mirroring the server availability engine and
  // the eagleEye add-reservation close check (ENG-571). Kiosk duration pickers add it to the derived
  // close before gating duration options.
  // finalReservationSlotBufferSeconds: area-resolved stop-seating buffer (null = venue opted out). The
  // kiosk gates the START against the walk-in cutoff (close + grace − finalSlotBuffer), matching
  // AddReservationAsync, so it never offers a duration whose start the backend would reject.
  async getVenueHoursByDate(corporationDetailId: string, dateISO: string, bookingAreaId?: string | null, retry = true): Promise<{ venueOpeningTime: string; venueClosingTime: string; reservationPastCloseBufferSeconds?: number; finalReservationSlotBufferSeconds?: number | null } | null> {
    let endpoint = `/spotlite-proxy/Widget/VenueHoursByDate?cid=${encodeURIComponent(corporationDetailId)}&dateISO=${encodeURIComponent(dateISO)}`;
    if (bookingAreaId) endpoint += `&bookingAreaId=${encodeURIComponent(bookingAreaId)}`;
    return this.get<{ venueOpeningTime: string; venueClosingTime: string; reservationPastCloseBufferSeconds?: number; finalReservationSlotBufferSeconds?: number | null }>(endpoint, retry);
  }

  async getReservationDailyBayByDefaultBayId(defaultBayId: string, reservationId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/ReservationDailyBayByDefaultBayId?dbid=${encodeURIComponent(defaultBayId)}&rid=${encodeURIComponent(reservationId)}`;
    return this.get<any>(endpoint, retry);
  }

  async checkRequestingUserMeetsAllRequirements(phone: string, cid: string, retry = true): Promise<boolean> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/RequestingUserMeetsAllRequirements?phone=${encodeURIComponent(phone)}&cid=${encodeURIComponent(cid)}`;
    return this.get<boolean>(endpoint, retry);
  }

  async getNextReservationTime(defaultBayId: string, corporationDetailId: string, retry = true): Promise<{ nextReservationTime: string | null } | null> {
    const endpoint = `/spotlite-proxy/Bay/NextReservationTime?dbid=${encodeURIComponent(defaultBayId)}&cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get<{ nextReservationTime: string | null }>(endpoint, retry);
  }

  async getBayAvailability(defaultBayId: string, corporationDetailId: string, retry = true): Promise<BayAvailabilityResponse | null> {
    const endpoint = `/spotlite-proxy/Bay/Availability?dbid=${encodeURIComponent(defaultBayId)}&cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get<BayAvailabilityResponse>(endpoint, retry);
  }

  // bookingAreaId: when set, backend filters floors to that area only (excluding null-area floors).
  async getKioskAvailableLocations(body: { corporationDetailId: string; reservationTime: string; secondsBooked: number; totalGuests: number; bookingAreaId?: string | null }, retry = true): Promise<any[] | null> {
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/availableLocations`;
    return this.post<any[]>(endpoint, body, retry);
  }

  async getFutureTimeSlots(corporationDetailId: string, totalGuests: number, durationSeconds: number, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/FutureTimeSlots?cid=${encodeURIComponent(corporationDetailId)}&totalGuests=${totalGuests}&durationSeconds=${durationSeconds}`;
    return this.get<any>(endpoint, retry);
  }

  // bookingAreaId: when set, backend filters floors to that area only (excluding null-area floors).
  async getFutureBayAvailability(body: { corporationDetailId: string; reservationTime: string; secondsBooked: number; totalGuests: number; bookingAreaId?: string | null }, retry = true): Promise<any[] | null> {
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/FutureBayAvailability`;
    return this.post<any[]>(endpoint, body, retry);
  }

  async getExistingReservationsForKiosk(phoneNumber: string, corporationDetailId: string, retry = true): Promise<any[] | null> {
    const digitsOnly = (phoneNumber ?? '').toString().trim().replace(/\D/g, '');
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/SearchReservationsByPhone?phone=${encodeURIComponent(digitsOnly)}&cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get<any[]>(endpoint, retry);
  }

  async getAvailableTimesByDate(body: { selectedDay: string; startTimeISO: string; corporationDetailId: string; partySize: number }, retry = true): Promise<any[] | null> {
    const endpoint = `/spotlite-proxy/Widget/GetAvailableTimesByDate`;
    return this.post<any[]>(endpoint, body, retry);
  }

  // bookingAreaId: when set, the backend resolves area-scoped hours (and area cache key segment)
  // so the returned slots respect area opening/closing times.
  async getKioskAvailableTimesByDate(body: { selectedDay: string; startTimeISO: string; corporationDetailId: string; partySize: number; bookingAreaId?: string | null }, retry = true): Promise<any[] | null> {
    const endpoint = `/spotlite-proxy/Widget/GetKioskAvailableTimesByDate`;
    return this.post<any[]>(endpoint, body, retry);
  }

  async getVenueDetails(corporationDetailId: string, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Widget/GetVenueDetails?cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get<any>(endpoint, retry);
  }

  async createPendingReservation(body: any, retry = true): Promise<{ reservationId: string } | null> {
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/CreatePendingReservation`;
    return this.post<{ reservationId: string }>(endpoint, body, retry);
  }

  async instantiateDueUpfrontPaymentIntent(body: any, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/DueUpfrontPaymentIntent`;
    return this.post<any>(endpoint, body, retry);
  }

  // Added helpers for kiosk booking terminal payment + check-in
  async getAmountRemainingWithFees(reservationId: string, retry = true): Promise<{ amountRemaining: number }> {
    const endpoint = `/spotlite-proxy/Reservation/ReservationAmountRemainingWithFees?rid=${encodeURIComponent(reservationId)}`;
    return this.get<{ amountRemaining: number }>(endpoint, retry);
  }

  // EAG-1452: `giftCardAppliedAmount` carries the gift amount already applied at quote so the server treats
  // `amount` as the card residual and defers gift-card redemption to the capture webhook (instead of
  // re-applying + immediately redeeming the gift card against the residual, which drained the card at $0-to-card).
  // EAG-1813: `source` names the calling surface. The Adyen collect call has always sent one; the
  // Stripe call never did, so when the Metro Golf 2026-08-11 loss was traced back there was no way to
  // tell which kiosk screen posted the request. Two investigations (2026-07-30 and 2026-08-11) both
  // stalled on that, and the 07-30 write-up recorded a surface as "confirmed" on evidence that cannot
  // actually distinguish the two components. Send it so the next occurrence is one query.
  async collectReservationPaymentOnTerminal(body: { reservationId: string; corporationDetailId: string; readerId: string; amount: number; applicationFeeAmount?: number; purchaseType?: string; currency?: string; captureMethod?: string; giftCardCode?: string | null; giftCardCodes?: string[] | null; giftCardAppliedAmount?: number | null; source?: string }, retry = true): Promise<any> {
    const endpoint = `/spotlite-proxy/Stripe/Terminal/Payment/Collect/Reservation`;
    return this.post<any>(endpoint, body, retry);
  }

  /**
   * Adyen counterpart to {@link collectReservationPaymentOnTerminal}. Resolves on the server to
   * the kiosk's bound PosTerminal → AdyenTerminalReference (POIID) and pushes a card-present
   * payment via the existing `IReservationAdyenTerminalPaymentService` flow. Completion lands via
   * the same SignalR `OnTerminalReaderActionUpdated` signal Stripe uses.
   *
   * EAG-1757: the gift card fields mirror {@link collectReservationPaymentOnTerminal} exactly.
   * `amount` is the CARD residual and `giftCardAppliedAmount` is the gift already netted out of it
   * at quote, so the server can reconstruct the full amount due today, re-size the split through
   * GiftCardTenderPlanner, and defer redemption to the capture webhook. Before this the Adyen path
   * had no gift card handling at all: the terminal was charged the residual and the gift card was
   * never debited.
   */
  async collectReservationPaymentOnAdyenTerminal(body: { reservationId: string; corporationDetailId: string; posTerminalId: string; amount: number; tipAmount?: number; source?: string; giftCardCode?: string | null; giftCardCodes?: string[] | null; giftCardAppliedAmount?: number | null }, retry = true): Promise<any> {
    const endpoint = `/spotlite-proxy/Spotlite/Adyen/Terminal/Payment/Collect/Reservation`;
    return this.post<any>(endpoint, body, retry);
  }

  async cancelReservationPayment(corporationDetailId: string, reservationId: string, readerId: string, retry = true): Promise<void> {
    const endpoint = `/spotlite-proxy/Stripe/Terminal/Reader/Payment/Cancel`;
    const body = { corporationDetailId, readerId, reservationId };
    return this.put<void>(endpoint, body, retry);
  }

  async getReservationDetails(corporationDetailId: string, reservationId: string, retry = true): Promise<{
    corporationUserId: string;
    maleGuests: number;
    femaleGuests: number;
    otherGuests: number;
    reservationStartTime: string;
    reservationEndTime: string;
    hasEndTimeManuallySet: boolean;
    secondsBooked?: number;
    toBayBufferSeconds: number;
    bookingAreaId?: string;
  }> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/GetReservationDetails?reservationId=${encodeURIComponent(reservationId)}&cid=${encodeURIComponent(corporationDetailId)}`;
    return this.get(endpoint, retry);
  }

  async kioskCheckInReservation(body: any, retry = true): Promise<any> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/CheckInReservation`;
    return this.post<any>(endpoint, body, retry);
  }

  public async withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation = 'Operation'): Promise<T> {
    let timeoutHandle: any;
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutHandle = setTimeout(() => reject(new Error(`${operation} timed out after ${timeoutMs}ms`)), timeoutMs);
      });

      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  async confirmKioskReservation(body: {
    reservationId: string;
    paymentIntentId?: string;
    stripeCustomerId?: string;
    giftCardCode?: string;
    giftCardAppliedAmount?: number;
    giftCardApplications?: { code: string; appliedAmount: number }[];
    selectedBayIds?: string[];
    kioskDeviceId?: string;
  }, retry = true): Promise<any> {
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/ConfirmReservation`;
    return this.post<any>(endpoint, body, retry);
  }

  async getAssignedBayNames(reservationId: string, retry = true): Promise<string[]> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Kiosk/AssignedBayNames?reservationId=${encodeURIComponent(reservationId)}`;
    return this.get<string[]>(endpoint, retry);
  }

  // ! PUT Requests
  async closeReservation(body: any, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/CloseReservation`;
    return this.put<any>(endpoint, body, retry);
  }

  async requestBayHelp(body: BayNeedsHelpRequest, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/NeedsHelp`;
    return this.put<any>(endpoint, body, retry);
  }

  async additionalGuestsatisfiedRequirements(body: AdditionalGuestSatisfiedRequirementsRequest, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/AdditionalGuestSatisfiedRequirements`;
    return this.put<any>(endpoint, body, retry);
  }

  async overrideActivationAsync(body: object, retry = true): Promise<any | null> {
    const endpoint = `/spotlite-proxy/Spotlite/InBay/Override/Activation`;
    return this.put<any>(endpoint, body, retry);
  }

  /**
   * Searches for guest profiles by phone number for kiosk guest lookup
   * @param phoneNumber The phone number to search for
   * @param corporationDetailId The venue corporation detail ID
   * @param retry Whether to retry on failure
   * @returns Promise with array of matching guest profiles
   */
  async searchGuestsByPhone(phoneNumber: string, corporationDetailId: string, retry = true): Promise<KioskGuestSearchDto[] | null> {
    const digitsOnly = (phoneNumber ?? '').toString().trim().replace(/\D/g, '');
    const endpoint = `/spotlite-proxy/Spotlite/KioskBooking/SearchGuestsByPhone?phoneNumber=${encodeURIComponent(digitsOnly)}&corporationDetailId=${encodeURIComponent(corporationDetailId)}`;
    return this.get<KioskGuestSearchDto[]>(endpoint, retry);
  }
  
}